import * as entityLoaders from './entityLoaders'
import * as commonEntityLoaders from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders'
import { useCallback } from 'react'
import { useNotifications } from '@sequencehq/core-components'
import { ApiData } from '../adapters/quoteTemplate.adapters.in'
import { logError } from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { MerchantBranding } from 'modules/Cube/domain/cube.domain.types'
import { INITIAL_CUBE_STATE } from 'modules/Cube/domain/cube.constants'
import {
  CorePortErrors,
  CubePortImplementationProp
} from 'modules/Cube/domain/cube.domain.types.ts'

type UseLoadQuoteTemplateEditor = () => (
  ctx: CubePortImplementationProp
) => (
  quoteTemplateId?: string
) => Promise<{ data: ApiData | null; error: CorePortErrors | null }>

export const useLoadQuoteTemplateEditor: UseLoadQuoteTemplateEditor = () => {
  const { displayNotification } = useNotifications()
  const quoteTemplateLoader = entityLoaders.useLoadQuoteTemplate()
  const productsLoader = commonEntityLoaders.useLoadProducts()
  const enabledCurrenciesLoader = commonEntityLoaders.useLoadEnabledCurrencies()
  const userLoader = entityLoaders.useLoadUser()

  const loadQuoteTemplate = useCallback(
    () =>
      async (
        quoteTemplateId?: string
      ): Promise<{
        data: Pick<ApiData, 'quoteTemplate' | 'presentation'> | null
        error: CorePortErrors | null
      }> => {
        if (!quoteTemplateId) {
          return {
            data: null,
            error: CorePortErrors.Other
          }
        }

        const loadedQuoteTemplate = await quoteTemplateLoader(quoteTemplateId)

        if (!loadedQuoteTemplate.data || loadedQuoteTemplate.error) {
          return {
            data: null,
            error: loadedQuoteTemplate.error || CorePortErrors.Other
          }
        }

        return {
          data: {
            quoteTemplate: loadedQuoteTemplate.data,
            presentation: loadedQuoteTemplate.data.template.presentation
          },
          error: null
        }
      },
    [quoteTemplateLoader]
  )

  const load = useCallback(
    () => async (quoteTemplateId?: string) => {
      try {
        const loadedQuoteTemplate = await loadQuoteTemplate()(quoteTemplateId)

        if (!loadedQuoteTemplate.data || loadedQuoteTemplate.error) {
          return {
            data: null,
            error: loadedQuoteTemplate.error || CorePortErrors.Other
          }
        }

        const { quoteTemplate, presentation } = loadedQuoteTemplate.data

        const [enabledCurrenciesData, createdByUser, merchantResponse] =
          await Promise.all([
            enabledCurrenciesLoader(),
            userLoader(quoteTemplate.template.createdBy),
            dashboard20240730Client.getMerchantForSequenceAccount()
          ])

        const merchantBranding: MerchantBranding | null = merchantResponse.data
          ? {
              legalCompanyName: merchantResponse.data.legalCompanyName,
              address: merchantResponse.data.address,
              email: merchantResponse.data.email
            }
          : null

        const pricesData =
          quoteTemplate.phases
            ?.flatMap(({ prices }) => prices)
            .map(price => ({
              ...price,
              currency: quoteTemplate.template.currency as Currency
            })) ?? []
        const productsLoaderData = await productsLoader({
          prices: pricesData
        })

        return {
          data: {
            quoteTemplate,
            presentation,
            prices: pricesData,
            products: productsLoaderData.products,
            currencies: enabledCurrenciesData,
            merchantBranding:
              merchantBranding ?? INITIAL_CUBE_STATE.data.merchantBranding,
            createdByUser,
            /**
             * These collections are always empty for templates.
             */
            customers: [],
            integrations: [],
            contacts: []
          },
          error: null
        }
      } catch (e) {
        displayNotification("Couldn't load quote template", {
          type: 'error'
        })

        logError(e)

        return {
          data: null,
          error: CorePortErrors.Other
        }
      }
    },
    [
      enabledCurrenciesLoader,
      displayNotification,
      loadQuoteTemplate,
      productsLoader,
      userLoader
    ]
  )

  return load
}
